import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../design-system';

interface Item {
  payment_intent_id: string;
  stripe_refunded_cents: number;
  recorded_cents: number;
  outside_cents: number;
  payments: { id: string; host_name: string | null; city: string | null; amount_cents: number; refunded_amount_cents: number; currency: string; status: string }[] | null;
}

function euro(cents: number, currency = 'eur'): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

function ItemCard({ item, onChanged }: { item: Item; onChanged: () => void }) {
  const payments = (item.payments ?? []).filter((p) => p.amount_cents - p.refunded_amount_cents > 0);
  const [paymentId, setPaymentId] = useState(payments[0]?.id ?? '');
  const chosen = payments.find((p) => p.id === paymentId);
  const max = chosen ? Math.min(item.outside_cents, chosen.amount_cents - chosen.refunded_amount_cents) : 0;
  const [amount, setAmount] = useState((max / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currency = item.payments?.[0]?.currency ?? 'eur';

  function pick(id: string) {
    setPaymentId(id);
    const p = payments.find((x) => x.id === id);
    if (p) setAmount((Math.min(item.outside_cents, p.amount_cents - p.refunded_amount_cents) / 100).toFixed(2));
  }

  async function run(fn: () => PromiseLike<{ error: { message: string } | null }>) {
    setBusy(true);
    setError(null);
    const { error: err } = await fn();
    setBusy(false);
    if (err) { setError(err.message || "We couldn't save that."); return; }
    onChanged();
  }

  const cents = Math.round(Number(amount) * 100);
  const valid = Boolean(chosen) && Number.isFinite(cents) && cents >= 1 && cents <= max;

  return (
    <li className="rounded-lg bg-app-bg border border-app-border p-3 flex flex-col gap-2">
      <p className="text-sm text-app-fg">
        Stripe shows <strong>{euro(item.stripe_refunded_cents, currency)}</strong> refunded on charge{' '}
        <span className="font-mono text-xs">{item.payment_intent_id}</span>; Sekretly has recorded {euro(item.recorded_cents, currency)}.
        <strong> {euro(item.outside_cents, currency)}</strong> is not on any payment.
      </p>
      {payments.length > 0 ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={`pay-${item.payment_intent_id}`} className="text-[11px] text-app-muted">It was for</label>
            <select id={`pay-${item.payment_intent_id}`} value={paymentId} onChange={(e) => pick(e.target.value)}
              className="bg-app-card border border-app-border rounded-lg text-xs px-2 py-1.5">
              {payments.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.city ? `${p.city} · ` : ''}{p.host_name ?? 'Unknown host'} · {euro(p.amount_cents - p.refunded_amount_cents, p.currency)} left
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`amt-${item.payment_intent_id}`} className="text-[11px] text-app-muted">Amount (€)</label>
            <input id={`amt-${item.payment_intent_id}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="bg-app-card border border-app-border rounded-lg text-xs px-2 py-1.5 w-24" />
          </div>
          <Button size="sm" disabled={busy || !valid}
            onClick={() => void run(() => supabase.rpc('admin_assign_outside_refund', { p_payment_intent: item.payment_intent_id, p_payment_id: paymentId, p_amount_cents: cents }))}>
            {busy ? <Spinner size="sm" className="text-ink" /> : 'Record on this payment'}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy}
            onClick={() => void run(() => supabase.rpc('admin_dismiss_outside_refund', { p_payment_intent: item.payment_intent_id }))}>
            Nothing to change
          </Button>
        </div>
      ) : (
        <div>
          <Button size="sm" variant="ghost" disabled={busy}
            onClick={() => void run(() => supabase.rpc('admin_dismiss_outside_refund', { p_payment_intent: item.payment_intent_id }))}>
            Nothing to change
          </Button>
        </div>
      )}
      {valid === false && chosen && amount !== '' && <p className="text-xs text-app-muted">Up to {euro(max, currency)} for this payment.</p>}
      {error && <p role="alert" className="text-xs text-crimson">{error}</p>}
    </li>
  );
}

/**
 * Refunds made outside Sekretly (the Stripe dashboard). Stripe says how much a charge
 * has had refunded; what the payment rows don't account for is listed here so an
 * admin can say which city it was for. Shows nothing when everything matches.
 */
export function OutsideRefunds({ onChanged }: { onChanged?: () => void }) {
  const [items, setItems] = useState<Item[] | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.rpc('admin_unmatched_refunds');
    setItems((data as Item[] | null) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!items || items.length === 0) return null;
  return (
    <section aria-label="Refunds made outside Sekretly" className="rounded-xl border border-gold/40 bg-gold/10 p-4 mb-5">
      <h2 className="text-sm font-semibold text-app-fg">Refunds made outside Sekretly ({items.length})</h2>
      <p className="text-xs text-app-muted mt-1 mb-3">
        These were refunded in Stripe directly. Say which payment each was for, so the traveler, host and payout records stay right.
      </p>
      <ul className="flex flex-col gap-2">
        {items.map((it) => (
          <ItemCard key={it.payment_intent_id} item={it} onChanged={() => { void load(); onChanged?.(); }} />
        ))}
      </ul>
    </section>
  );
}
